import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import {
  DocumentsService,
  SyncAdminService,
  SyncDeviceView,
  SyncMappingConfig,
} from '../../../../services/openapi';
import { Idir } from '../../../../List/FileList_Components';
import { mappingIdentity, parseMappingImport } from './mappingImport';

interface IMappingsDialogProps {
  device: SyncDeviceView | null;
  onClose: () => void;
  onSaved: () => void;
}

function parseMappings(device: SyncDeviceView | null): SyncMappingConfig[] {
  if (!device?.mappings) return [];
  try {
    return JSON.parse(device.mappings);
  } catch {
    return [];
  }
}

/**
 * Folders-only tree node. Children are loaded lazily (one level at a time) as
 * the admin expands the tree - see {@link ServerFolderPicker}.
 */
function FolderNode({
  node,
  depth,
  expanded,
  loading,
  onToggle,
  onPick,
}: {
  node: Idir;
  depth: number;
  expanded: Set<string>;
  loading: Set<string>;
  onToggle: (node: Idir) => void;
  onPick: (prefix: string) => void;
}) {
  const isOpen = expanded.has(node.hidrive_id);
  const isLoading = loading.has(node.hidrive_id);
  const childFolders = (node.children || []).filter((c) => c.type === 'dir');
  return (
    <>
      <ListItemButton sx={{ pl: 2 + depth * 2 }} onClick={() => onPick(node.hidrive_id)}>
        <IconButton
          size="small"
          sx={{ mr: 0.5 }}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(node);
          }}>
          {isLoading ? (
            <CircularProgress size={16} />
          ) : isOpen ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <ListItemIcon sx={{ minWidth: 34 }}>
          {isOpen && childFolders.length > 0 ? <FolderOpenIcon color="primary" /> : <FolderIcon color="primary" />}
        </ListItemIcon>
        <ListItemText primary={node.parent} />
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List disablePadding>
          {childFolders.map((child) => (
            <FolderNode
              key={child.hidrive_id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              loading={loading}
              onToggle={onToggle}
              onPick={onPick}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
}

/** Replace a node's children inside the (possibly nested) forest, immutably. */
function attachChildren(forest: Idir[], parentId: string, children: Idir[]): Idir[] {
  return forest.map((f) => {
    if (f.hidrive_id === parentId) return { ...f, children };
    if (f.children && f.children.length > 0) {
      return { ...f, children: attachChildren(f.children, parentId, children) };
    }
    return f;
  });
}

/**
 * Picks any server folder as a sync destination. Loads the bucket's top-level
 * folders first (so every parent folder is selectable, not just Hausverwaltung)
 * and fetches each folder's children on demand via the lightweight
 * getDirContent endpoint - the old getFiles call serialized the entire 10k-node
 * tree and was very slow.
 */
function ServerFolderPicker({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick: (prefix: string) => void }) {
  const { call_backend_api } = React.useContext(APIWrapperContext);
  const [folders, setFolders] = React.useState<Idir[] | null>(null);
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [loaded, setLoaded] = React.useState<Set<string>>(new Set());
  const [loading, setLoading] = React.useState<Set<string>>(new Set());

  const onlyDirs = (res: Idir): Idir[] => (res.children || []).filter((c) => c.type === 'dir');

  React.useEffect(() => {
    if (!open) return;
    setFolders(null);
    setExpanded(new Set());
    setLoaded(new Set());
    setLoading(new Set());
    call_backend_api(
      DocumentsService.hidriveGetFilesDocGetDirContentGet(undefined, false),
      (res: Idir) => setFolders(onlyDirs(res)),
      () => setFolders([]),
      undefined,
      false
    );
  }, [open, call_backend_api]);

  const removeFrom = (s: Set<string>, id: string) => {
    const next = new Set(s);
    next.delete(id);
    return next;
  };

  const loadChildren = (node: Idir) => {
    setLoading((prev) => new Set(prev).add(node.hidrive_id));
    call_backend_api(
      DocumentsService.hidriveGetFilesDocGetDirContentGet(node.hidrive_id, false),
      (res: Idir) => {
        setFolders((prev) => (prev ? attachChildren(prev, node.hidrive_id, onlyDirs(res)) : prev));
        setLoaded((prev) => new Set(prev).add(node.hidrive_id));
        setLoading((prev) => removeFrom(prev, node.hidrive_id));
      },
      () => setLoading((prev) => removeFrom(prev, node.hidrive_id)),
      undefined,
      false
    );
  };

  const toggle = (node: Idir) => {
    const willExpand = !expanded.has(node.hidrive_id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(node.hidrive_id)) next.delete(node.hidrive_id);
      else next.add(node.hidrive_id);
      return next;
    });
    if (willExpand && !loaded.has(node.hidrive_id)) loadChildren(node);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Server-Ordner wählen</DialogTitle>
      <DialogContent sx={{ minHeight: 300 }}>
        {folders === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : folders.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            Keine Ordner gefunden.
          </Typography>
        ) : (
          <List dense disablePadding>
            {folders.map((node) => (
              <FolderNode
                key={node.hidrive_id}
                node={node}
                depth={0}
                expanded={expanded}
                loading={loading}
                onToggle={toggle}
                onPick={(prefix) => {
                  onPick(prefix);
                  onClose();
                }}
              />
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function MappingsDialog({ device, onClose, onSaved }: IMappingsDialogProps) {
  const { call_backend_api } = React.useContext(APIWrapperContext);
  const [mappings, setMappings] = React.useState<SyncMappingConfig[]>([]);
  const [scanInterval, setScanInterval] = React.useState(10);
  const [pickerForIndex, setPickerForIndex] = React.useState<number | null>(null);
  const [importResult, setImportResult] = React.useState<{
    severity: 'success' | 'warning' | 'error';
    message: string;
  } | null>(null);

  React.useEffect(() => {
    setMappings(parseMappings(device));
    setScanInterval(device?.scan_interval_minutes ?? 10);
    setImportResult(null);
  }, [device]);

  const updateMapping = (index: number, patch: Partial<SyncMappingConfig>) => {
    setMappings((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const addMapping = () => {
    setMappings((prev) => [
      ...prev,
      { id: crypto.randomUUID(), localPath: '', s3Prefix: '', enabled: true },
    ]);
  };

  const importMappings = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;

    try {
      if (file.size > 2 * 1024 * 1024) {
        setImportResult({
          severity: 'error',
          message: 'Die Importdatei ist größer als 2 MB.',
        });
        return;
      }

      const result = parseMappingImport(await file.text());
      if (result.mappings.length === 0) {
        setImportResult({
          severity: 'error',
          message: result.errors[0] || 'Es wurden keine gültigen Zuordnungen gefunden.',
        });
        return;
      }

      let existingDuplicateCount = 0;
      const identities = new Set(
        mappings
          .filter((mapping) => mapping.localPath.trim() && mapping.s3Prefix.trim())
          .map(mappingIdentity)
      );
      const imported: SyncMappingConfig[] = [];

      result.mappings.forEach((mapping) => {
        const identity = mappingIdentity(mapping);
        if (identities.has(identity)) {
          existingDuplicateCount += 1;
          return;
        }
        identities.add(identity);
        imported.push({
          id: crypto.randomUUID(),
          localPath: mapping.localPath,
          s3Prefix: mapping.s3Prefix,
          enabled: true,
        });
      });
      setMappings((previous) => [...previous, ...imported]);

      const skippedCount =
        result.duplicateCount + existingDuplicateCount + result.errors.length;
      setImportResult({
        severity: result.errors.length > 0 ? 'warning' : 'success',
        message:
          `${imported.length} Zuordnung${imported.length === 1 ? '' : 'en'} aus "${file.name}" importiert.` +
          (skippedCount > 0
            ? ` ${skippedCount} ungültige oder doppelte ${skippedCount === 1 ? 'Zuordnung wurde' : 'Zuordnungen wurden'} übersprungen.`
            : '') +
          ' Zum Übernehmen bitte speichern.',
      });
    } catch {
      setImportResult({
        severity: 'error',
        message: 'Die Importdatei konnte nicht gelesen werden.',
      });
    } finally {
      input.value = '';
    }
  };

  const save = () => {
    if (!device) return;
    const cleaned = mappings.filter((m) => m.localPath.trim() !== '' && m.s3Prefix.trim() !== '');
    call_backend_api(
      SyncAdminService.updateSyncDeviceSyncAdminDevicesDeviceIdPatch(device.id, {
        mappings: cleaned,
        scan_interval_minutes: scanInterval,
      }),
      () => {
        onSaved();
        onClose();
      },
      undefined,
      undefined,
      true,
      'Ordner-Zuordnungen gespeichert',
      'Fehler beim Speichern der Zuordnungen!'
    );
  };

  return (
    <Dialog open={device !== null} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Ordner-Zuordnungen – {device?.name}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Der lokale Pfad gilt auf dem Kunden-PC (z.B. <code>D:\Scans</code> oder{' '}
          <code>\\server\freigabe</code>). Die Sync-App holt sich diese Einstellungen automatisch.
        </Typography>
        {importResult && (
          <Alert
            severity={importResult.severity}
            onClose={() => setImportResult(null)}
            sx={{ mb: 2 }}>
            {importResult.message}
          </Alert>
        )}
        {mappings.map((mapping, index) => (
          <Paper key={mapping.id} variant="outlined" sx={{ p: 2, mb: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Lokaler Pfad auf dem Kunden-PC"
              size="small"
              sx={{ flexGrow: 1, minWidth: 220 }}
              value={mapping.localPath}
              onChange={(e) => updateMapping(index, { localPath: e.target.value })}
              placeholder="D:\Scans"
            />
            <TextField
              label="Server-Ordner"
              size="small"
              sx={{ flexGrow: 1, minWidth: 220 }}
              value={mapping.s3Prefix}
              InputProps={{ readOnly: true }}
              onClick={() => setPickerForIndex(index)}
              placeholder="Klicken zum Auswählen…"
            />
            <Tooltip title="Zuordnung aktiv">
              <Switch
                checked={mapping.enabled}
                onChange={(e) => updateMapping(index, { enabled: e.target.checked })}
              />
            </Tooltip>
            <IconButton onClick={() => setMappings((prev) => prev.filter((_, i) => i !== index))}>
              <DeleteIcon />
            </IconButton>
          </Paper>
        ))}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mt: 2,
            flexWrap: 'wrap',
          }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button startIcon={<AddCircleOutlineIcon />} onClick={addMapping}>
              Zuordnung hinzufügen
            </Button>
            <Button component="label" startIcon={<UploadFileIcon />}>
              Aus Datei importieren
              <input
                hidden
                type="file"
                accept=".txt,text/plain"
                onChange={importMappings}
              />
            </Button>
          </Box>
          <TextField
            label="Scan-Intervall (Minuten)"
            size="small"
            type="number"
            sx={{ width: 180 }}
            value={scanInterval}
            onChange={(e) => setScanInterval(Math.max(1, Number(e.target.value) || 10))}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Importformat: <code>Lokaler Pfad -&gt; Server-Ordner;</code> (eine oder mehrere
          Zuordnungen pro Datei). Importierte Zuordnungen werden an die Liste angehängt.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={save}>
          Speichern
        </Button>
      </DialogActions>

      <ServerFolderPicker
        open={pickerForIndex !== null}
        onClose={() => setPickerForIndex(null)}
        onPick={(prefix) => {
          if (pickerForIndex !== null) updateMapping(pickerForIndex, { s3Prefix: prefix });
        }}
      />
    </Dialog>
  );
}
